import { useState } from 'react';
import BackendService from '../../services/BackendService';
import useAppContext from '../useAppContext/useAppContext';
import { useInterval } from 'react-use';

const useCurrentSessionState = (delay = 5000) => {
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const [currentState, setCurrentState] = useState<any>({});
  const [initialState, setInitialState] = useState<any>(undefined);

  useInterval(() => {
    BackendService.getCurrentState(sessionId, treatmentId)
      .then((result: any) => {
        if (!result) {
          return null;
        }
        if (!initialState) {
          setInitialState(result);
        } else {
          setCurrentState(result);
        }
      })
      .catch(e => {
        console.error(e);
      });
  }, delay);

  const {
    mediaType,
    rotation,
    mediaProjection,
    displayMode,
    mediaStereoMode,
    playbackType,
    mediaState,
    loadProgress,
    errorMessage,
    token,
    generatedUUID,
    headsetWorn,
    wifiQuality,
    wifiSpeed,
    batteryHeadset,
    batteryController1,
    batteryController2,
    timestamp,
    serialNumber,
    version = '1.0.0',
    waitingForDeviceCheck: waitingForDeviceCheckRaw,
    isWaitingForDeviceCheck: isWaitingForDeviceCheckRaw,
  } = currentState;

  // If it hasn't polled yet, empty return;
  if (!initialState) {
    return {};
  }

  const normalizeBoolean = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      if (value === 'true' || value === '1') return true;
      if (value === 'false' || value === '0') return false;
    }
    return undefined;
  };

  const waitingForDeviceCheck =
    normalizeBoolean(waitingForDeviceCheckRaw) ?? normalizeBoolean(isWaitingForDeviceCheckRaw) ?? false;

  let isConnected = false;

  if (timestamp) {
    // @ts-ignore
    const diff = Math.abs(new Date() - new Date(timestamp));
    const minutes = Math.round(((diff % 86400000) % 3600000) / 60000);
    isConnected = minutes <= 1;
  }
  // Ignore the initial state (first poll);
  if (JSON.stringify(initialState) == JSON.stringify(currentState)) {
    return {
      token,
      mediaState,
      generatedUUID,
      headsetWorn,
      wifiQuality,
      wifiSpeed,
      batteryHeadset,
      batteryController1,
      batteryController2,
      timestamp,
      isConnected,
      serialNumber,
      waitingForDeviceCheck,
      version,
    };
  }

  const tokenToReturn = initialState.token !== token ? token : null;

  if (hasDevice) {
    return {
      token: tokenToReturn,
      errorMessage,
      generatedUUID,
      headsetWorn,
      wifiQuality,
      wifiSpeed,
      batteryHeadset,
      batteryController1,
      batteryController2,
      timestamp,
      isConnected,
      serialNumber,
      waitingForDeviceCheck,
      mediaState,
      loadProgress,
      mediaType,
      rotation,
      mediaProjection,
      displayMode,
      mediaStereoMode,
      playbackType,
      version,
    };
  }

  return {
    mediaType,
    rotation,
    mediaProjection,
    displayMode,
    mediaStereoMode,
    playbackType,
    mediaState,
    loadProgress,
    errorMessage,
    headsetWorn,
    wifiQuality,
    wifiSpeed,
    batteryHeadset,
    batteryController1,
    batteryController2,
    isConnected,
    serialNumber,
    waitingForDeviceCheck,
    token: tokenToReturn,
    version,
  };
};

export default useCurrentSessionState;
